import { Component, ViewChild } from '@angular/core';
import {
  Content,
  IonicPage,
  Loading,
  LoadingController,
  NavController
} from 'ionic-angular';

import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { WebCamComponent } from 'ack-angular-webcam';

import { AlertProvider } from '../../providers/alert/alert';
import { ProofProvider } from '../../providers/proof/proof';
import { ProofServerProvider } from '../../providers/proof-server/proof-server';
import { MediaProvider } from '../../providers/media/media';
import { DateTimeProvider } from '../../providers/datetime/datetime';
import { ProofAudioProvider } from '../../providers/proof-audio/proof-audio';

/**
 * Generated class for the PlatformExaminationPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */
@IonicPage()
@Component({
  selector: 'page-platform-examination',
  templateUrl: 'platform-examination.html',
})
export class PlatformExaminationPage {
  @ViewChild(Content) content: Content;
  @ViewChild('selfieWebcam') selfieWebcam: WebCamComponent;

  loader: Loading;
  detectingWebcam: boolean = true;
  buttonContinue: boolean = false;
  hasWebcam: boolean = true;
  isWebcamRequired: boolean = false;

  selfieColor: string = 'danger';
  selfieBase64: string = null;

  frontIdentificationColor: string = 'danger';
  frontIdentificationWebcam: WebCamComponent;
  frontIdentificationBase64: string = null;

  backIdentificationColor: string = 'danger';
  backIdentificationWebcam: WebCamComponent;
  backIdentificationBase64: string = null;

  image: string = null;

  fileDocument: any;
  fileSelfie: any;

  fileDocumentUpload: File = null;
  fileSelfieUpload: File = null;

  isRecording: boolean = false;
  recordedTime: string;
  blobUrl: SafeUrl = null;

  options: any = {};

  constructor(
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
    private alertProvider: AlertProvider,
    private mediaProvider: MediaProvider,
    private proofServerProvider: ProofServerProvider,
    private proofProvider: ProofProvider,
    private dateTimeProvider: DateTimeProvider,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private proofAudioProvider: ProofAudioProvider,
  ) {
    this.fileDocument = null;
    this.fileSelfie = null;

    // this.isWebcamRequired = this.proofProvider.isWebcamRequired();
    this.isWebcamRequired = false;

    this.proofAudioProvider.recordingFailed().subscribe((): void => {
      this.isRecording = false;
    });

    this.proofAudioProvider.getRecordedTime().subscribe((time: string): void => {
      this.recordedTime = time;
    });

    this.proofAudioProvider.getRecordedBlob().subscribe((data: any): void => {
      if (data && data.blob) {
        this.blobUrl = this.sanitizer.bypassSecurityTrustUrl(
          URL.createObjectURL(data.blob)
        );
      }
    });
  }

  ionViewDidLoad(): void {
    console.log('ionViewDidLoad PlatformExaminationPage');

    this.loader = this.loadingCtrl.create({
      content: 'Verificando se existe webcam e sua permissão de acesso...'
    });

    this.loader.present();

    this.detectWebcam((hasWebcam: boolean): void => {
      console.log('Webcam: ' + (hasWebcam ? 'yes' : 'no'));
      this.hasWebcam = hasWebcam;
      this.detectingWebcam = false;

      if (this.loader) {
        this.loader.dismiss();
      }
    });
  }

  public goBack(): void {
    this.navCtrl.pop();
  }

  public onCamError(err: any): void {
    console.log(err);

    if (this.hasWebcam) {
      this.hasWebcam = false;
    }

    if (this.content) {
      this.content.scrollToTop();
    }

    this.proofServerProvider.unlock(this.proofProvider.getId()).subscribe({
      next: (x: any) => {
        console.log('Observer got a next value: ' + x);
        this.dismissLoaderSafely();
      },
      error: (error: any) => {
        console.log('Observer got an error: ' + error);
        this.dismissLoaderSafely();
      },
      complete: () => {
        console.log('Observer got a complete notification');
        this.dismissLoaderSafely();
      },
    });
  }

  public onCamSuccess(event?: any): void {
    console.log('Webcam success', event);
  }

  public takeSelfie(): void {
    if (this.hasWebcam && this.selfieColor === 'danger') {
      this.takeWebcamPicture(this.selfieWebcam);
    }
  }

  public takeWebcamPicture(webcam: WebCamComponent): void {
    if (!webcam) {
      console.error('Webcam component not available.');
      return;
    }

    webcam.getBase64()
      .then((data: string) => {
        if (!data) {
          return;
        }

        const image = new Image();

        image.onload = () => {
          const canvas: HTMLCanvasElement = document.createElement('canvas');
          const maxSize = 800;

          let width = image.width;
          let height = image.height;

          if (width > height) {
            if (width > maxSize) {
              height = height * (maxSize / width);
              width = maxSize;
            }
          } else if (height > maxSize) {
            width = width * (maxSize / height);
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;

          const context = canvas.getContext('2d');

          if (!context) {
            console.error('Could not get canvas context.');
            return;
          }

          context.drawImage(image, 0, 0, width, height);

          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.75);

          this.selfieBase64 = resizedBase64;
          this.image = resizedBase64;
          this.selfieColor = 'primary';
        };

        image.onerror = (error: any) => {
          console.error('Error loading image for resizing.', error);
        };

        image.src = data;
      })
      .catch((error: any) => {
        console.error(error);
      });
  }

  private detectWebcam(callback: (hasWebcam: boolean) => void): void {
    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices || !mediaDevices.enumerateDevices) {
      callback(false);
      return;
    }

    mediaDevices.enumerateDevices()
      .then((devices: MediaDeviceInfo[]) => {
        callback(devices.some((device: MediaDeviceInfo) => device.kind === 'videoinput'));
      })
      .catch((error: any) => {
        console.error('Error detecting webcam', error);
        callback(false);
      });
  }

  public startRecording(): void {
    if (!this.isRecording) {
      this.isRecording = true;
      this.proofAudioProvider.startRecording();

      setTimeout((): void => {
        this.stopRecording();
      }, 10000);
    }
  }

  public stopRecording(): void {
    if (this.isRecording) {
      const questionId = null;
      const kind: string = 'PA';
      const note = null;
      const extra = null;
      const captured: string = this.dateTimeProvider.getISOString();
      const seconds = null;
      const secondsRemaining = null;

      this.proofAudioProvider.stopRecording(
        questionId,
        kind,
        note,
        extra,
        captured,
        seconds,
        secondsRemaining
      );

      this.isRecording = false;
    }
  }

  public clearRecordedData(): void {
    this.blobUrl = null;
  }

  private dismissLoaderSafely(): void {
    if (this.loader) {
      this.loader.dismiss().catch(() => {});
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/platform-examination/platform-examination.ts